import type { SchoolReportDto } from '../../../api/generated/types.gen';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface SchoolDataPreviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    data: SchoolReportDto | null;
    isLoading: boolean;
}

/**
 * SchoolDataPreviewDialog
 *
 * Displays school details including basic information, professor contact details, and collaboration protocol PDFs.
 * Used in Edition Status Report to preview school data for ADMIN/DBEST roles.
 *
 * Created 2025-11-07: Replicates user preview pattern for schools
 * - Displays school summary (name, SIIIR code, CUI, legal representative, etc.)
 * - Shows professor information (name, email, phone, code)
 * - Protocol PDFs guaranteed to display from professor.protocolDeColaborare field
 *
 * @example
 * <SchoolDataPreviewDialog
 *   isOpen={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   data={schoolProfile}
 *   isLoading={false}
 * />
 */
export function SchoolDataPreviewDialog({
    isOpen,
    onClose,
    data,
    isLoading
}: SchoolDataPreviewDialogProps) {
    const hasProtocols = data?.professor?.protocolDeColaborare && data.professor.protocolDeColaborare.length > 0;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Previzualizare Detalii Școală</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="space-y-4 py-4">
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                    </div>
                ) : !data ? (
                    <div className="flex justify-center items-center py-8">
                        <span className="text-small text-gray-500">Nu există date disponibile</span>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* School Information Section */}
                        <div>
                            <h3 className="ecotic-dialog-section-title">
                                Informații Școală
                            </h3>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="col-span-2">
                                    <label className="text-small font-medium text-gray-700">Nume Instituție:</label>
                                    <p className="text-small text-gray-900 font-medium">{data.numeInstitutieInvatamant || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Cod SIIIR:</label>
                                    <p className="text-medium break-words text-gray-900">{data.codSiiirUnitate || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Cod SE:</label>
                                    <p className="text-medium break-words text-gray-900">{data.codSe || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">CUI:</label>
                                    <p className="text-medium break-words text-gray-900">{data.cui || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Număr Elevi:</label>
                                    <p className="text-medium break-words text-gray-900">{data.numarElevi || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-small font-medium text-gray-700">Reprezentant Legal:</label>
                                    <p className="text-medium break-words text-gray-900">{data.numeReprezentatLegal || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Telefon:</label>
                                    <p className="text-medium break-words text-gray-900">{data.telefon || '-'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Professor Information Section */}
                        {data.professor && (
                            <div>
                                <h3 className="ecotic-dialog-section-title">
                                    Informații Profesor
                                </h3>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Nume Complet:</label>
                                        <p className="text-medium break-words text-gray-900">{data.professor.nume || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Email:</label>
                                        <p className="text-medium break-words text-gray-900">{data.professor.email || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Telefon:</label>
                                        <p className="text-medium break-words text-gray-900">{data.professor.telefon || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Cod Profesor:</label>
                                        <p className="text-medium break-words text-gray-900">{data.professor.codProfesor || '-'}</p>
                                    </div>
                                </div>

                                {/* Protocol PDFs - GUARANTEED DISPLAY */}
                                {hasProtocols && (
                                    <div className="mt-4">
                                        <label className="mb-2 block text-small font-medium text-gray-700">
                                            Protocol de Colaborare:
                                        </label>
                                        <div className="space-y-2 bg-gray-50 p-3 rounded-md">
                                            {data.professor.protocolDeColaborare!.map((docUrl, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => window.open(docUrl, '_blank')}
                                                    className="flex min-h-11 items-center text-small font-medium text-magenta hover:underline md:min-h-0"
                                                >
                                                    📄 Descarcă Protocol {index + 1}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Close Button */}
                        <div className="ecotic-dialog-footer">
                            <button
                                onClick={onClose}
                                className="flex h-11 items-center rounded-md border border-input bg-white px-4 text-small font-medium text-gray-700 hover:bg-gray-50 md:h-9"
                            >
                                Închide
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
